import { useTranslation } from 'react-i18next';

import { DataPanel, DataPanelAutoWrapInlineItem, DataPanelInlineItem, DataPanelItem, DataPanelRow } from '@/ui';
import type { Caller, Classification, Patient } from '@/signalr';
import { medicDataAge } from '../../model/incidentDomain';
import { formatHeight, formatWeight } from '../../model/formatters';
import { CallerClassificationPanel } from './CallerClassificationPanel';
import { CallerFullNamePanel } from './CallerFullNamePanel';
import { ClosePersonContactPanel } from './ClosePersonContactPanel';
import { MedicDataPanelItem } from './MedicDataPanelItem';
import { PhoneNumberListPanel } from './PhoneNumberListPanel';

export type MobileAppCallerInfoPanelProps = {
  incidentId: string;
  incidentIsReadOnly: boolean;
  caller: Caller;
  patient: Patient | null;
  classification: Classification | null;
  createdTimeUtc: string;
};

export function MobileAppCallerInfoPanel({
  incidentId,
  incidentIsReadOnly,
  caller,
  patient,
  classification,
  createdTimeUtc,
}: MobileAppCallerInfoPanelProps) {
  const { t } = useTranslation();

  return (
    <DataPanel header={t('Incident_InfoPanel_MobileAppName')} expandable={!incidentIsReadOnly}>
      <CallerClassificationPanel classification={classification} />

      <CallerFullNamePanel incidentId={incidentId} callerFullName={caller.fullname} />

      <PhoneNumberListPanel incidentId={incidentId} phoneNumbers={caller.phoneNumbers} />

      <DataPanelInlineItem title={t('Incident_InfoPanel_InsuranceNumber')} value={patient?.insuranceNumber ?? undefined} />

      <DataPanelRow>
        <DataPanelItem
          title={t('Incident_InfoPanel_Age')}
          value={medicDataAge(patient?.medicData?.yearOfBirth ?? null, createdTimeUtc)?.toString()}
        />

        <DataPanelItem title={t('Incident_InfoPanel_CallerHeight')} value={formatHeight(patient?.medicData?.height)} />

        <DataPanelItem title={t('Incident_InfoPanel_CallerWeight')} value={formatWeight(patient?.medicData?.weight)} />
      </DataPanelRow>

      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_PermanentResidence')}
        showEmptyPlaceholderWhen={() => caller.permanentResidence == null}
      >
        <div>{caller.permanentResidence?.fullAddress}</div>
        <div>{caller.permanentResidence?.hint}</div>
      </DataPanelAutoWrapInlineItem>

      {patient?.medicData != null && <MedicDataPanelItem medicData={patient.medicData} />}

      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_TemporaryInformation')}
        value={patient?.medicData?.temporaryInformation?.message}
        nonEmptyValueColor="critical"
        showEmptyPlaceholder
      />

      <ClosePersonContactPanel contacts={caller.contacts} />
    </DataPanel>
  );
}
